import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import InputSection from './Components/InputSection';
import SalutationSection from './Components/SalutationSection';

import './Profile.scss';

const spinnerId = 'profile-settings-profile';

const INPUTS = [
  {
    name: 'profile-settings-profile-firstName',
    label: ProfileSettingsConfig.Labels.firstName,
    fieldName: 'firstName',
    title: ProfileSettingsConfig.LinkText.firstName,
    maxLength: 50,
    minLength: 2,
    isRequired: true,
  },
  {
    name: 'profile-settings-profile-lastName',
    label: ProfileSettingsConfig.Labels.lastName,
    fieldName: 'lastName',
    title: ProfileSettingsConfig.LinkText.lastName,
    maxLength: 50,
    minLength: 2,
    isRequired: true,
  },
  {
    name: 'profile-settings-profile-jobTitle',
    label: ProfileSettingsConfig.Labels.jobTitle,
    fieldName: 'jobTitle',
    title: ProfileSettingsConfig.LinkText.jobTitle,
    maxLength: 50,
    minLength: 3,
    isRequired: false,
  },
  {
    name: 'profile-settings-profile-institution',
    label: ProfileSettingsConfig.Labels.institution,
    fieldName: 'institution',
    title: ProfileSettingsConfig.LinkText.institution,
    maxLength: 50,
    minLength: 3,
    isRequired: false,
  },
  {
    name: 'profile-settings-profile-location',
    label: ProfileSettingsConfig.Labels.location,
    fieldName: 'location',
    title: ProfileSettingsConfig.LinkText.location,
    maxLength: 50,
    minLength: 3,
    isRequired: false,
  },
];

function Profile(props) {
  const { isLoading, inputData, changeInputData, resetInputData, saveInputData, init, trackAdobeAnalyticsError, allSalutations } = props;

  useEffect(() => {
    RouteChangeTracking.trackOnRouteChangeSuccess();
    init();
    // eslint-disable-next-line
  }, []);

  const handleInputChange = (fieldName, value) => {
    changeInputData({ [fieldName]: value || '' });
  };

  const handleInputClose = (fieldName) => {
    resetInputData(fieldName);
  };

  const handleInputSave = (fieldName) => {
    saveInputData(fieldName);
  };

  const handleInputBlur = (fieldName, errors) => {
    trackAdobeAnalyticsError(fieldName, errors);
  };

  return (
    <div
      className="rx-accordion rx-accordion--large profile-settings-accordion profile-accordion"
      role="form"
      aria-label="Profile Settings">
      <div>
        {isLoading && <SimpleSpinner id={spinnerId} />}
        <SalutationSection
          name="profile-settings-profile-salutation"
          label={ProfileSettingsConfig.Labels.salutation}
          title={ProfileSettingsConfig.LinkText.salutation}
          value={inputData.salutation}
          options={allSalutations}
          onSave={() => handleInputSave('salutation')}
          onChange={(value) => handleInputChange('salutation', value)}
          onClose={() => handleInputClose('salutation')}
        />
        {INPUTS.map((input) => (
          <InputSection
            key={input.name}
            name={input.name}
            label={input.label}
            value={inputData[input.fieldName]}
            title={input.title}
            minLength={input.minLength}
            isRequired={input.isRequired}
            maxLength={input.maxLength}
            onSave={() => handleInputSave(input.fieldName)}
            onChange={(value) => handleInputChange(input.fieldName, value)}
            onClose={() => handleInputClose(input.fieldName)}
            onBlur={(errors) => handleInputBlur(input.fieldName, errors)}
          />
        ))}
      </div>
    </div>
  );
}

Profile.propTypes = {
  isLoading: PropTypes.bool,
  inputData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    jobTitle: PropTypes.string,
    location: PropTypes.string,
    institution: PropTypes.string,
    salutation: PropTypes.string,
  }),
  changeInputData: PropTypes.func.isRequired,
  resetInputData: PropTypes.func.isRequired,
  saveInputData: PropTypes.func.isRequired,
  init: PropTypes.func.isRequired,
  trackAdobeAnalyticsError: PropTypes.func.isRequired,
  allSalutations: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, salutation: PropTypes.string })),
};

export default Profile;
